import { PlanType } from 'data/plan-config';

type PaidPlanType = Exclude<PlanType, 'free'>;

/**
 * sessionStorage key used to remember the plan a couple chose on the
 * landing page while they walk through signup → email verification.
 *
 * The Stripe upgrade flow requires a verified email, so we cannot kick
 * it off at signup time. Instead we stash the chosen plan here and the
 * VerifyEmail page picks it up after a successful verification.
 */
export const PENDING_PLAN_STORAGE_KEY = 'pendingPlan';

export const setPendingPlan = (plan: PaidPlanType): void => {
  try {
    sessionStorage.setItem(PENDING_PLAN_STORAGE_KEY, plan);
  } catch {
    // sessionStorage may be unavailable (private mode, etc.) — fail silently;
    // the user can still upgrade from Billing & Plan after verifying.
  }
};

export const consumePendingPlan = (): PaidPlanType | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_PLAN_STORAGE_KEY);
    if (raw) sessionStorage.removeItem(PENDING_PLAN_STORAGE_KEY);
    if (raw === 'basic' || raw === 'pro') return raw;
    return null;
  } catch {
    return null;
  }
};

/**
 * Read the pending plan without removing it from storage. Use this when
 * you only need to know whether a paid upgrade is queued (e.g. to render
 * an informational banner).
 */
export const peekPendingPlan = (): PaidPlanType | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_PLAN_STORAGE_KEY);
    if (raw === 'basic' || raw === 'pro') return raw;
    return null;
  } catch {
    return null;
  }
};

/**
 * sessionStorage key used to mark that the upgrade about to happen is
 * part of a brand-new signup (landing page → create event → verify →
 * Stripe), as opposed to an existing couple upgrading from Billing &
 * Plan. The dashboard reads this after Stripe redirects back so it can
 * show a welcome-flavoured success message instead of the upgrade one.
 */
export const SIGNUP_UPGRADE_FLAG_KEY = 'signupUpgradeInProgress';

export const markSignupUpgrade = (): void => {
  try {
    sessionStorage.setItem(SIGNUP_UPGRADE_FLAG_KEY, '1');
  } catch {
    // see setPendingPlan
  }
};

/**
 * Read the signup-upgrade flag without removing it. The dashboard reads it
 * while rendering and clears it in an effect (clearSignupUpgradeFlag): Strict
 * Mode renders twice on mount, and a read that removed the flag would leave the
 * render React keeps with `false`.
 */
export const peekSignupUpgradeFlag = (): boolean => {
  try {
    return sessionStorage.getItem(SIGNUP_UPGRADE_FLAG_KEY) === '1';
  } catch {
    return false;
  }
};

export const clearSignupUpgradeFlag = (): void => {
  try {
    sessionStorage.removeItem(SIGNUP_UPGRADE_FLAG_KEY);
  } catch {
    // see setPendingPlan
  }
};
